import * as React from "react";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import * as _ from "lodash";
import Editor from "../../components/Editing/Editor";
import { nameofFactory, getNotificationObject, hasWriteAccess } from "../../utils/utils";
import { EditActions, editActions } from "../../actions/editActions";
import TextField from "../../components/Editing/TextField";
import NumberField from "../../components/Editing/NumberField";
import ToggleField from "../../components/Editing/ToggleField";
import MenuButton from "../../components/MenuButton/MenuButton";
import { info } from "react-notification-system-redux";

type StateProps = {
  editedStandardPoint: Api.StandardPoint;
  storedStandardPoint: Api.StandardPoint;
  clipboard: State.ClipBoard;
  pdApi: State.PdApi;
  ioConfig: State.IoConfig;
  activeUser: Api.User | undefined;
};

type DispatchProps = {
  editActions: typeof editActions;
  showInfo: typeof info;
};

type OwnProps = {
  toggleConfirm: () => void;
};

const standardBoardFields = nameofFactory<Api.StandardPoint>();

export type EditStandardPointProps = StateProps & DispatchProps & OwnProps;

export class EditStandardPoint extends React.Component<EditStandardPointProps> {
  state = {
    hasCopied: false
  };
  componentDidUpdate(prevProps: Api.AnyItem): void {
    if (prevProps.editedStandardPoint.id !== this.props.editedStandardPoint.id) {
      this.setState({ hasCopied: false });
    }
  }
  getActions = (): Api.StandardAction[] | null | undefined => {
    const actions = this.props.ioConfig.standardPoints.find(
      point => point.id === this.props.editedStandardPoint.id
    )?.standardActions;
    return actions;
  };
  standardActions = this.getActions();
  copyActions = (): void => {
    if (this.standardActions) {
      this.props.showInfo(getNotificationObject("info", "Actions copied to clipboard"));
      this.setState({ hasCopied: true });
      this.props.editActions.copyObject({
        option: "standardActions",
        data: this.standardActions
      });
    }
  };
  pasteActions = (): void => {
    if (this.props.clipboard.option === "standardActions") {
      this.props.showInfo(
        getNotificationObject("info", "Actions pasted to current Point, save to confirm")
      );
      this.props.editActions.pasteObject();
    } else {
      this.props.showInfo(
        getNotificationObject("info", "Can't paste - Wrong data type in clipboard")
      );
    }
  };
  render(): JSX.Element {
    const {
      editedStandardPoint,
      storedStandardPoint,
      activeUser,
      toggleConfirm,
      clipboard
    } = this.props;
    const isNew = !storedStandardPoint;

    return (
      <Editor
        itemName={"Standard Point"}
        originalObject={storedStandardPoint}
        editedObject={editedStandardPoint}
        hasWritePermission={hasWriteAccess(activeUser, "Lift")}
        isValid={this.isAllFieldsValid()}
        cancel={this.props.editActions.cancelEditing}
        update={() => this.props.editActions.update("standardPoints")}
        create={() => this.props.editActions.add("standardPoints")}
        remove={{ callback: toggleConfirm, disabled: isNew }}
        copy={() =>
          this.props.editActions.startEditing(
            { ...editedStandardPoint, ioname: editedStandardPoint.ioname + " COPY" },
            true
          )
        }
      >
        <div className="point-editor-row">
          <NumberField
            id="index"
            value={editedStandardPoint.ioindex}
            label="Index"
            isError={!this.isFieldValid(standardBoardFields("ioindex"))}
            onChange={(ioindex: number) =>
              this.props.editActions.setEdited({ ...editedStandardPoint, ioindex })
            }
          />
          <ToggleField
            labels={{ left: "Input", right: "Output" }}
            id="isInput"
            checked={!editedStandardPoint.isInput}
            onClick={() =>
              this.props.editActions.setEdited({
                ...editedStandardPoint,
                isInput: !editedStandardPoint.isInput
              })
            }
          />
        </div>
        <TextField
          id="name"
          label="name"
          value={editedStandardPoint.ioname}
          isError={!this.isFieldValid(standardBoardFields("ioname"))}
          onChange={(ioname: string) =>
            this.props.editActions.setEdited({ ...editedStandardPoint, ioname })
          }
        />

        {hasWriteAccess(activeUser, "Lift") && (
          <>
            <h3>Manage I/O Actions</h3>
            <div className="editor-action-list">
              <MenuButton
                type="Add"
                handleOnClick={() =>
                  this.props.editActions.addAction(editedStandardPoint.id, "standardActions")
                }
                customText="ADD ACTION"
                disabled={isNew}
              />
              {this.standardActions && (
                <MenuButton
                  type="Copy"
                  handleOnClick={() => {
                    this.copyActions();
                  }}
                  customText="COPY ALL ACTION(S)"
                  disabled={isNew || this.state.hasCopied}
                />
              )}
              <MenuButton
                type="Paste"
                handleOnClick={() => {
                  this.pasteActions();
                }}
                customText="PASTE ACTION(S)"
                disabled={
                  editedStandardPoint?.standardActions?.length !==
                    storedStandardPoint?.standardActions?.length ||
                  clipboard?.option !== "standardActions"
                }
              />
              {clipboard?.option === "standardActions" && (
                <p>There are {clipboard.data.length} action(s) in clipboard</p>
              )}
            </div>
          </>
        )}
      </Editor>
    );
  }

  private isFieldValid = (field: string): boolean => {
    const { editedStandardPoint } = this.props;
    switch (field) {
      case standardBoardFields("ioindex"):
        return editedStandardPoint.ioindex >= 0;
      case standardBoardFields("ioname"):
        return editedStandardPoint.ioname.trim().length > 0;
      default:
        return true;
    }
  };

  private isAllFieldsValid = (): boolean => {
    const { editedStandardPoint } = this.props;
    const temp = _.keys(editedStandardPoint);
    for (const key of temp) {
      if (!this.isFieldValid(key)) {
        return false;
      }
    }
    return true;
  };
}

const mapStateToProps = (state: State.Root) => ({
  editedStandardPoint: state.editView.editedObject,
  storedStandardPoint: state.editView.storedObject,
  clipboard: state.editView.clipboard,
  pdApi: state.pdApi,
  ioConfig: state.ioConfig,
  activeUser: state.main.activeUser
});

const mapDispatchToProps = (dispatch: Dispatch<EditActions>) => ({
  editActions: bindActionCreators({ ...editActions }, dispatch),
  showInfo: bindActionCreators(info, dispatch)
});

export default connect(mapStateToProps, mapDispatchToProps)(EditStandardPoint);


